import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Plus, Search, Pencil, Trash2, Power, UsersRound } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/services/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { useSingleFlightMutation } from "@/hooks/useSingleFlightMutation";

const EMPTY = {
  name: "",
  email: "",
  phone: "",
  pin: "",
  active: true,
  notify: true,
  can_manage: true,
};

export default function TeamPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const { data: members = [], isLoading } = useQuery({
    queryKey: ["team"],
    queryFn: async () => (await api.get("/team")).data,
  });

  const filtered = useMemo(
    () => members.filter((m) =>
      !search
      || (m.name || "").toLowerCase().includes(search.toLowerCase())
      || (m.email || "").toLowerCase().includes(search.toLowerCase())
      || (m.phone || "").includes(search.replace(/\s/g, ""))
    ),
    [members, search]
  );

  const openCreate = () => { setEditing(null); setForm(EMPTY); setModalOpen(true); };
  const openEdit = (m) => { setEditing(m); setForm({ ...EMPTY, ...m, pin: m.pin || "" }); setModalOpen(true); };

  const saveMut = useSingleFlightMutation({
    mutationFn: async () => {
      const payload = { ...form };
      if (!payload.pin) delete payload.pin;
      if (editing) return (await api.put(`/team/${editing.id}`, payload)).data;
      return (await api.post("/team", payload)).data;
    },
    onSuccess: (data) => {
      toast.success(editing ? "Manm modifye" : `Manm kreye — PIN: ${data.pin}`);
      qc.invalidateQueries({ queryKey: ["team"] });
      setModalOpen(false);
    },
    onError: (err) => toast.error(formatApiError(err.response?.data?.detail)),
  });

  const deleteMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/team/${id}`)).data,
    onSuccess: () => { toast.success("Manm efase"); qc.invalidateQueries({ queryKey: ["team"] }); },
  });

  const toggleActive = useMutation({
    mutationFn: async (m) => (await api.put(`/team/${m.id}`, { active: !m.active })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["team"] }),
  });

  return (
    <div className="space-y-6">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-heading font-bold flex items-center gap-2">
            <UsersRound className="h-8 w-8 text-brand-blue" /> Team
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-xl">
            Ajoute moun ki ka manage transfè sou Bot Tach Admin epi resevwa notifikasyon.
            Yo bezwen <strong>email + PIN 4 chif</strong> pou Telegram, epi <strong>nimewo WhatsApp</strong>
            pou bot la rekonèt yo (pa trete yo kòm ajan).
          </p>
        </div>
        <Button onClick={openCreate} data-testid="add-team-btn" className="bg-brand-blue hover:bg-brand-blueHover text-white gap-2">
          <Plus className="h-4 w-4" /> Ajoute manm
        </Button>
      </motion.div>

      <div className="rounded-2xl border border-border bg-card">
        <div className="p-4 border-b border-border flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Chèche non oswa email"
              className="pl-9 h-9"
              data-testid="team-search-input"
            />
          </div>
          <div className="text-xs text-muted-foreground">{filtered.length} manm</div>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Non</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>WhatsApp</TableHead>
                <TableHead>PIN</TableHead>
                <TableHead>Notif</TableHead>
                <TableHead>Manage</TableHead>
                <TableHead>Telegram</TableHead>
                <TableHead>Estati</TableHead>
                <TableHead className="text-right">Aksyon</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && [...Array(2)].map((_, i) => (
                <TableRow key={i}><TableCell colSpan={9}><Skeleton className="h-8" /></TableCell></TableRow>
              ))}
              {!isLoading && filtered.length === 0 && (
                <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-10">Pa gen manm team</TableCell></TableRow>
              )}
              {filtered.map((m) => (
                <TableRow key={m.id} data-testid={`team-row-${m.id}`}>
                  <TableCell className="font-medium">{m.name}</TableCell>
                  <TableCell className="font-mono text-sm">{m.email}</TableCell>
                  <TableCell className="font-mono text-sm">{m.phone || "—"}</TableCell>
                  <TableCell className="font-mono text-sm tracking-widest" data-testid={`team-pin-${m.id}`}>{m.pin || "—"}</TableCell>
                  <TableCell>{m.notify !== false ? "Wi" : "Non"}</TableCell>
                  <TableCell>{m.can_manage !== false ? "Wi" : "Non"}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {m.telegram_chat_id || "Pokò — /start sou Bot Tach Admin"}
                  </TableCell>
                  <TableCell>
                    <Badge variant={m.active ? "default" : "outline"} className={m.active ? "bg-emerald-500/20 text-emerald-500 border-emerald-500/30" : ""}>
                      {m.active ? "Aktif" : "Inaktif"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button variant="ghost" size="icon" onClick={() => toggleActive.mutate(m)} data-testid={`toggle-team-${m.id}`}>
                      <Power className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => openEdit(m)} data-testid={`edit-team-${m.id}`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => window.confirm("Efase manm sa a?") && deleteMut.mutate(m.id)} data-testid={`delete-team-${m.id}`}>
                      <Trash2 className="h-4 w-4 text-rose-500" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-card max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Modifye manm" : "Ajoute manm team"}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4">
            <div>
              <Label>Non</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="team-form-name" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="team-form-email" />
            </div>
            <div>
              <Label>Nimewo WhatsApp</Label>
              <Input
                value={form.phone || ""}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="+19132964949"
                data-testid="team-form-phone"
                className="font-mono"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Bot WhatsApp la ap rekonèt nimewo sa a kòm team/admin (pa mande PIN ajan).
              </p>
            </div>
            <div>
              <Label>PIN 4 chif</Label>
              <Input
                inputMode="numeric"
                maxLength={4}
                value={form.pin || ""}
                onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "").slice(0, 4) })}
                placeholder={editing ? "Kenbe oswa chanje" : "Opsyonèl — auto si vid"}
                data-testid="team-form-pin"
                className="font-mono tracking-widest"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Manm lan: Bot Tach Admin → /start → email → PIN → meni manage.
              </p>
            </div>
            <div className="flex items-center justify-between rounded-xl border border-border px-3 py-2">
              <div>
                <div className="text-sm font-medium">Notifikasyon</div>
                <div className="text-xs text-muted-foreground">Resevwa alèt Bot Notifikasyon</div>
              </div>
              <Switch checked={!!form.notify} onCheckedChange={(v) => setForm({ ...form, notify: v })} data-testid="team-form-notify" />
            </div>
            <div className="flex items-center justify-between rounded-xl border border-border px-3 py-2">
              <div>
                <div className="text-sm font-medium">Manage transfè</div>
                <div className="text-xs text-muted-foreground">Itilize Bot Tach Admin</div>
              </div>
              <Switch checked={!!form.can_manage} onCheckedChange={(v) => setForm({ ...form, can_manage: v })} data-testid="team-form-manage" />
            </div>
            <div className="flex items-center justify-between rounded-xl border border-border px-3 py-2">
              <div className="text-sm font-medium">Aktif</div>
              <Switch checked={!!form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="team-form-active" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>Anile</Button>
            <Button
              className="bg-brand-blue hover:bg-brand-blueHover text-white"
              disabled={!form.email.trim() || saveMut.isPending}
              onClick={() => saveMut.mutate()}
              data-testid="team-form-save"
            >
              Sove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
